/**
 * Phone input split into a dial-code ``<select>`` + number ``<input>``.
 *
 * Replaces the hardcoded ``PHONE_PREFIXES`` array previously inlined
 * in ``Checkout.tsx`` (CZ / SK / DE / AT / PL / GB / US — fixed for
 * every tenant). The prefix options are now derived from the
 * tenant's enabled delivery countries, mirroring whatever the
 * admin selected in Krok 01 of ``/admin/shipping``. Distinct
 * dial codes only — multiple countries sharing a code (e.g. US + CA
 * both ``+1``) collapse to one option.
 *
 * The ``linkedCountryCode`` prop ties this input to a sibling
 * ``CountrySelect``: when the user picks a country, the prefix
 * auto-snaps to that country's dial code — but only while the user
 * hasn't manually overridden the prefix this session. This keeps
 * the "I picked CZ, of course I want +420" UX without trapping
 * users who explicitly want a different prefix.
 *
 * The wire-format value passed into ``onChange`` is the full
 * international form ``"+<dialcode> <local>"`` (with single space)
 * — same shape the BE ``checkout.AddressIn.phone`` regex expects.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type CheckoutCountry,
  useCheckoutDeliveryCountries,
} from "@/api/queries/checkout/deliveryCountries";

interface PhoneInputProps {
  /** Full international phone, e.g. ``"+420 777 123 456"``. */
  value: string;
  onChange: (next: string) => void;
  /** Linked country code (ISO-2). When changed, prefix auto-syncs. */
  linkedCountryCode?: string;
  required?: boolean;
  name?: string;
  testId?: string;
  /** Default prefix when ``value`` is blank and no link is set. */
  fallbackDialCode?: string;
  className?: string;
}

interface PrefixOption {
  /** Stable key — first ISO-2 code that uses this dial code. */
  key: string;
  dialCode: string;
  flagEmoji: string;
}

export function PhoneInput({
  value,
  onChange,
  linkedCountryCode,
  required,
  name,
  testId,
  fallbackDialCode = "+420",
  className,
}: PhoneInputProps): JSX.Element {
  const { t } = useTranslation("checkout");
  const { data } = useCheckoutDeliveryCountries();

  // Distinct dial codes, ordered by the tenant's country sort_order.
  const prefixes: PrefixOption[] = useMemo(() => {
    const out: PrefixOption[] = [];
    const seen = new Set<string>();
    for (const c of data ?? []) {
      if (seen.has(c.dial_code)) continue;
      seen.add(c.dial_code);
      out.push({
        key: c.code,
        dialCode: c.dial_code,
        flagEmoji: c.flag_emoji,
      });
    }
    return out;
  }, [data]);

  // Did the user ever pick a prefix manually this session? If yes,
  // we stop auto-syncing from ``linkedCountryCode`` so we don't
  // override their choice on subsequent country flips.
  const userPickedPrefixRef = useRef(false);

  const initial = splitPhone(value, fallbackDialCode);
  const [prefix, setPrefix] = useState(initial.prefix);
  const [number, setNumber] = useState(initial.number);

  // Sync internal state when ``value`` changes from outside (e.g. a
  // form reset / "use saved address" prefill).
  useEffect(() => {
    const split = splitPhone(value, prefix);
    if (split.prefix !== prefix || split.number !== number) {
      setPrefix(split.prefix);
      setNumber(split.number);
      // External prefill counts as user intent — don't auto-flip.
      userPickedPrefixRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Auto-sync prefix from linked country code, unless the user
  // manually changed it.
  useEffect(() => {
    if (userPickedPrefixRef.current) return;
    if (!linkedCountryCode) return;
    const country = (data ?? []).find(
      (c: CheckoutCountry) => c.code === linkedCountryCode,
    );
    if (!country) return;
    if (country.dial_code === prefix) return;
    setPrefix(country.dial_code);
    onChange(joinPhone(country.dial_code, number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCountryCode, data]);

  const baseSelectClass =
    "rounded border border-border-strong bg-surface px-2 py-1.5 text-sm text-ink";
  const baseInputClass =
    "block w-full rounded border border-border-strong bg-surface px-2 py-1.5 text-sm text-ink";

  return (
    <div className={className ?? "flex gap-2"}>
      <select
        value={prefix}
        onChange={(e) => {
          const next = e.target.value;
          userPickedPrefixRef.current = true;
          setPrefix(next);
          onChange(joinPhone(next, number));
        }}
        aria-label={t("phoneInput.prefixLabel", "Telefonní předvolba")}
        className={baseSelectClass}
        data-testid={testId ? `${testId}-prefix` : undefined}
      >
        {prefixes.length === 0 ? (
          <option value={fallbackDialCode}>{fallbackDialCode}</option>
        ) : (
          prefixes.map((p) => (
            <option key={p.key} value={p.dialCode}>
              {p.flagEmoji} {p.dialCode}
            </option>
          ))
        )}
      </select>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        name={name}
        required={required}
        value={number}
        onChange={(e) => {
          const next = e.target.value;
          setNumber(next);
          onChange(joinPhone(prefix, next));
        }}
        placeholder={t("phoneInput.placeholder", "777 123 456")}
        className={baseInputClass}
        data-testid={testId}
      />
    </div>
  );
}

/**
 * Parse a stored phone string into its dial-code prefix and the
 * remaining local number. Falls back to ``fallback`` when no
 * recognised prefix is found (e.g. legacy address book entries
 * stored without one).
 */
function splitPhone(
  raw: string,
  fallback: string,
): { prefix: string; number: string } {
  const trimmed = (raw ?? "").trim();
  if (!trimmed.startsWith("+")) {
    return { prefix: fallback, number: trimmed };
  }
  // Match ``+<digits>`` greedily up to first whitespace; keep the
  // remainder verbatim so user-entered spaces inside the local part
  // survive a round-trip through the controlled input.
  const match = /^(\+\d+)\s*(.*)$/.exec(trimmed);
  if (!match) return { prefix: fallback, number: trimmed };
  return { prefix: match[1] ?? fallback, number: match[2] ?? "" };
}

function joinPhone(prefix: string, local: string): string {
  const trimmedLocal = (local ?? "").trim();
  if (!trimmedLocal) return prefix;
  return `${prefix} ${trimmedLocal}`;
}
